'use client';

import { useEffect, useState } from 'react';

interface AIProgressBarProps {
  /** Whether the AI process is currently running */
  active: boolean;
  /** Ordered status steps to cycle through */
  steps: string[];
  /** Size variant — onboarding has orbit animation */
  size?: 'lg' | 'sm' | 'onboarding';
}

export function AIProgressBar({ active, steps, size = 'lg' }: AIProgressBarProps) {
  const [progress, setProgress] = useState(0);
  const [stepIndex, setStepIndex] = useState(0);

  useEffect(() => {
    if (!active) {
      setProgress(0);
      setStepIndex(0);
      return;
    }

    setProgress(0);
    setStepIndex(0);

    const interval = setInterval(() => {
      setProgress((p) => {
        if (p >= 90) return p + 0.08;
        if (p >= 70) return p + 0.3;
        if (p >= 40) return p + 0.8;
        return p + 2;
      });
    }, 100);

    return () => clearInterval(interval);
  }, [active]);

  useEffect(() => {
    if (!active || steps.length <= 1) return;

    const interval = setInterval(() => {
      setStepIndex((i) => (i + 1) % steps.length);
    }, 2000);

    return () => clearInterval(interval);
  }, [active, steps.length]);

  useEffect(() => {
    if (!active && progress > 0) {
      setProgress(100);
      const timer = setTimeout(() => setProgress(0), 600);
      return () => clearTimeout(timer);
    }
  }, [active, progress]);

  if (!active) return null;

  if (size === 'onboarding') {
    return (
      <div className="animate-fade-in space-y-6">
        {/* Orbit animation */}
        <div className="flex justify-center">
          <div className="relative w-20 h-20">
            {/* Outer ring */}
            <div className="absolute inset-0 ai-orbit">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-2.5 h-2.5 rounded-full bg-primary/70" />
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-primary/30" />
            </div>
            {/* Inner ring */}
            <div className="absolute inset-3 ai-orbit-reverse">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-primary/50" />
              <div className="absolute right-0 top-1/2 -translate-y-1/2 w-1 h-1 rounded-full bg-primary/20" />
            </div>
            {/* Center dot */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-3 h-3 rounded-full bg-primary/40 animate-ai-pulse" />
            </div>
          </div>
        </div>

        {/* Progress bar */}
        <div className="w-full max-w-xs mx-auto">
          <div className="h-1.5 rounded-full overflow-hidden bg-base-300/30">
            <div
              className="h-full rounded-full ai-progress-bar transition-all duration-300 ease-out"
              style={{ width: `${Math.min(progress, 100)}%` }}
            />
          </div>
        </div>

        {/* Status text */}
        <p key={stepIndex} className="text-sm text-base-content/50 text-center animate-fade-in">
          {steps[stepIndex]}
        </p>
      </div>
    );
  }

  const isLarge = size === 'lg';

  return (
    <div className={`animate-fade-in ${isLarge ? 'space-y-2' : 'space-y-1'}`}>
      {/* Progress bar */}
      <div className={`w-full rounded-full overflow-hidden ${isLarge ? 'h-1.5' : 'h-1'} bg-base-300/30`}>
        <div
          className="h-full rounded-full ai-progress-bar transition-all duration-300 ease-out"
          style={{ width: `${Math.min(progress, 100)}%` }}
        />
      </div>

      {/* Status text */}
      <p key={stepIndex} className={`${isLarge ? 'text-xs' : 'text-[10px]'} text-base-content/40 animate-fade-in truncate`}>
        {steps[stepIndex]}
      </p>
    </div>
  );
}
